// Single swap point for "which tenant is the current client acting on" —
// the client-side mirror of functions/src/tenant.ts's
// resolveTrustedTenantId(). Neither file imports the other across the
// client/Functions boundary, so keep them in sync in spirit (the "one
// canonical mechanism, call sites don't change" contract), not necessarily
// value-identical code.
//
// Phase 5A: this file never talks to Firestore itself. AuthProvider
// resolves the caller's real tenant membership (users/{uid}.tenantIds) as
// part of its own async profile-resolution sequence and publishes it here
// via setResolvedTenantIds() — the same "module-level singleton populated
// by app startup" pattern src/firebase.ts already uses for auth/db.
// resolveActiveTenantId() then reads that state SYNCHRONOUSLY, so every
// existing non-React call site (src/lib/db.ts's settingsRef()/createAction())
// keeps working unchanged.

import { useSyncExternalStore } from 'react';
import { where, type QueryConstraint } from 'firebase/firestore';

/** null = "not yet resolved for this session" (AuthProvider hasn't settled,
 *  or the caller is signed out) — resolveActiveTenantId() throws rather
 *  than silently defaulting to Blackhawk when it sees this. */
let resolvedTenantIds: string[] | null = null;

/** Phase 5D — subscribers notified whenever resolvedTenantIds changes (a
 *  sign-in/out, or switchActiveTenant reordering it). Every hook that
 *  builds a tenant-scoped Firestore query off resolveActiveTenantId()/
 *  tenantWhere() needs to know when to recompute — a useMemo whose deps
 *  don't include this can silently keep querying whatever tenant was
 *  active when the component first mounted, since switching tenants is a
 *  live in-place update, never a remount. See useActiveTenantId() below. */
const listeners = new Set<() => void>();

/** AuthProvider-only: called once per resolved (or cleared) auth state,
 *  with the caller's OWN users/{uid}.tenantIds — never anything a client
 *  could otherwise influence. */
export function setResolvedTenantIds(tenantIds: string[] | null): void {
  resolvedTenantIds = tenantIds;
  for (const l of listeners) l();
}

function subscribeActiveTenantId(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function activeTenantIdSnapshot(): string | null {
  return resolvedTenantIds ? chooseTenantId(resolvedTenantIds) : null;
}

/** Reactive read of the active tenant id (tenantIds[0]) — the value every
 *  tenant-scoped query constraint is ultimately built from. Any hook that
 *  memoizes a tenantWhere()-containing constraints array MUST include this
 *  in its own dependency list, or switching tenants (functions/src/
 *  tenantSwitch.ts's switchActiveTenant, a live reorder with no page
 *  reload) leaves that query silently bound to the previous tenant. See
 *  useCollection.ts's key, and every useVisibleX / useScopedX hook in
 *  src/hooks/ for the pattern. */
export function useActiveTenantId(): string | null {
  return useSyncExternalStore(subscribeActiveTenantId, activeTenantIdSnapshot);
}

/** Deterministic multi-membership default: always tenantIds[0]. Phase 5A
 *  originally paired this with a localStorage-backed preference override
 *  ("a future selector UI just needs to call choosePreferredTenantId()") —
 *  removed in Phase 5D once a real selector (switchActiveTenant, see
 *  functions/src/tenantSwitch.ts) existed: firestore.rules' myTenantId()
 *  has no way to read localStorage, so a stored preference that diverged
 *  from tenantIds[0] would make the client query for one tenant while
 *  rules authorized against another — permission-denied on everything.
 *  The real switcher reorders tenantIds itself instead (the one field
 *  client and rules already both trust), so this function's job is just
 *  to read that same order — no client-only override, ever. */
export function chooseTenantId(tenantIds: string[]): string | null {
  return tenantIds.length > 0 ? tenantIds[0] : null;
}

/** Trusted, membership-derived active-tenant resolution (Phase 5A). Throws
 *  — loudly, never a silent Blackhawk fallback — if called before
 *  AuthProvider has resolved tenant membership for the current session, or
 *  for a signed-in caller with zero memberships. Every call site
 *  (settingsRef, createAction, …) is unchanged from Phase 1 — only this
 *  function's body changed. */
export function resolveActiveTenantId(): string {
  if (resolvedTenantIds === null) {
    throw new Error(
      'resolveActiveTenantId() called before tenant membership was resolved. This must only be called once AuthProvider has settled (profile loaded, not `loading`).',
    );
  }
  const tenantId = chooseTenantId(resolvedTenantIds);
  if (!tenantId) {
    throw new Error('resolveActiveTenantId() called for a caller with no tenant membership.');
  }
  return tenantId;
}

/** Phase 5B: the standard `where('tenantId', '==', ...)` constraint every
 *  tenant-owned operational query should include, built from the SAME
 *  trusted resolution as resolveActiveTenantId() — never a client-supplied
 *  value. Deliberately NOT applied inside useCollection() itself (a few
 *  collections it also serves, like the legacy email-keyed `users` profile
 *  plane and admin-global logs, are not tenant-owned at all and have no
 *  tenantId field to filter on) — each tenant-owned hook adds this
 *  explicitly, so the tenant boundary is visible at every call site rather
 *  than implicit/hidden in a shared hook. */
export function tenantWhere(): QueryConstraint {
  return where('tenantId', '==', resolveActiveTenantId());
}
